/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

/**
 * Exports AuthService.
 */
import { BehaviorSubject } from 'rxjs';
import { auth, EmailAuthProvider } from '../firebase.js';
import Router from '../router';
import { AnalyticsService } from './analytics.service.js';
import { GA_EVENTS } from "@/constants/gaConstants.js";
import { LTI_ROLES } from "@/constants/appConstants.js";
import { ConfigService } from '@/services/config.service';
import { CONFIG_TYPE } from "@/config/constants.js";

const USE_OPEN_ACCESS_DASHBOARD = process.env.VUE_APP_USE_OPEN_ACCESS_DASHBOARD == 'true';

/**
 * Define a service variable with a "loggedUser" property, which is a RxJS BehaviourSubject.
 * This is oinitialised with the default user, nd updated with logged in user when one authenticates.
 */
var service = {
    // Create a BehaviorSubject with a default value
    loggedUser: new BehaviorSubject(getDefaultUser())
};

/**
 * Return a default user with empty properties and display name "Guest".
 */
function getDefaultUser() {
    return {
        id: null,
        photoURL: null,
        roles: []
    };
}

/**
 * Construct a capitalised display name from the email address of a user.
 * Takes the string up to the '@' sign, replaces dots with spaces and capitalises individual words.
 * @param {*} email an aemail address
 */
function getDisplayNameFromEmail(email, uid) {

    if (!email) // lti authenticated does not always provide an email
    {

        let learnerid = uid.substring(uid.indexOf(':'));
        // extract LTI id from uid id
        return "Learner " + learnerid ;
    }

    let name = email.substring(0, email.indexOf('@')).replace(".", " ");
    let nameWithCapitals = name.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    return nameWithCapitals;
}

/**
 * A service to authenticate users. Also manages analytics preferences.
 */
const AuthService = {
    init: () => {
        console.info("Initialising AuthService");

        // An observer for changes to the signed-in user's ID token, which includes sign-in, sign-out, and token refresh events,
        // including analytics preference changes.
        // https://firebase.google.com/docs/reference/js/firebase.auth.Auth#onidtokenchanged
        // NOTE: "The token refreshes lazily when interacting with other Firebase services"
        // https://github.com/firebase/firebase-js-sdk/issues/2985#issuecomment-626985962
        auth.onIdTokenChanged(function (firebaseUser) {
            console.debug(" auth.service.onIdTokenChanged ");
            if (firebaseUser) {
                firebaseUser.getIdTokenResult(true).then(token => {
                    // Update the analytics consent:
                    var collectAnalytics = (typeof token.claims.analyticsAgreed !== 'undefined' && token.claims.analyticsAgreed === true);
                    AnalyticsService.enableAnalyticsCollection(collectAnalytics);
                });
            }
        });

        // An observer for changes to the user's sign-in state. Triggered on sign-in or sign-out.
        // https://firebase.google.com/docs/reference/js/firebase.auth.Auth#onauthstatechanged
        auth.onAuthStateChanged(function (firebaseUser) {
            console.debug(" auth.service.onAuthStateChanged ");
            if (firebaseUser) {
                // Construct a user object from firebaseUser properties and token claims
                firebaseUser.getIdTokenResult(true).then(token => {
                    let user = {
                        uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        displayName: firebaseUser.displayName ? firebaseUser.displayName : getDisplayNameFromEmail(firebaseUser.email, firebaseUser.uid),
                        photoURL: firebaseUser.photoURL,
                        orgId: token.claims.orgId,
                        roles: token.claims.roles,
                        termsAgreed: token.claims.termsAgreed,
                        termsVersion: token.claims.termsVersion,
                        analyticsAgreed: token.claims.analyticsAgreed,
                        isLtiUser: token.claims.roles?.some(r => LTI_ROLES.includes(r)) || false
                    };
                    console.info("UOE TEST: firebase user ");
                    // Emit a new user value to subscribers via loggedUser
                    service.loggedUser.next(user);
                    AnalyticsService.logAnalyticsEvent(GA_EVENTS.USER_LOGIN);
                    ConfigService.getOrgConfig(user.orgId).then(function (orgConfig) {
                        let orgCustomisation = null;
                        orgConfig ? orgCustomisation = orgConfig.getConfig(CONFIG_TYPE.CUSTOMISATION) : orgCustomisation = undefined;
                        let defaultWorkflow = ''
                        if (orgCustomisation) {
                            orgCustomisation['url.path.default.workflow'] ? defaultWorkflow = orgCustomisation['url.path.default.workflow'] : defaultWorkflow = ''
                        }

                        // Check if the user is assigned to an organisation, if we are on the home or sign in pages
                        if (Router.currentRoute.name === "Home" || Router.currentRoute.name === "SignIn") {
                            console.info("UOE TEST: route was either Home or SignIn ");
                            if (!user.orgId && !user.roles?.includes("superadmin")) {
                                console.error('User is not assigned any organisation');
                                alert('Your user is not assigned any organisation! Please contact the administrator.');
                                // Router.push({ name: "Home" });
                                // return;
                                AuthService.signOut();
                                return
                            }
                            Router.push({ path: `/workflow/${defaultWorkflow}` })
                            return;
                        }
                    }.bind(this));

                }, error => {
                    console.error("Could not get user token", error);
                });
            } else {
                console.debug("Auth service: onAuthStateChanged no user ");
                console.info("UOE TEST: Auth service: onAuthStateChanged no user - will use default user");
                // Emit a default user value to subscribers via loggedUser
                service.loggedUser.next(getDefaultUser());
            }
        });
        console.info("AuthService initialised");
    },
    signInWithEmailAndPassword: (email, password) => {
        // Try and shortcut the update to the analytics flag, as the AnalyticsService
        // doesn't necessarily receive the user login event update before the AuthService:
        // AnalyticsService.enableAnalyticsCollection(service.loggedUser.analyticsAgreed);
        return auth.signInWithEmailAndPassword(email, password);
    },
    signInWithCustomToken: (token) =>
    {
        console.info('Signing in with custom token')
        return auth.signInWithCustomToken(token)
    },
    signOut: () => {
        let userId = auth.currentUser.uid;
        AnalyticsService.logAnalyticsEvent(GA_EVENTS.USER_LOGOUT);
        // Update the analytics consent:
        AnalyticsService.enableAnalyticsCollection(false);

        auth.signOut().then(function () {
            console.log("Auth service: Signed Out "+userId);
            if (Router.currentRoute.name != "Home") {
                Router.push({ name: "Home" });
            }
        }, function (error) {
            console.error("Sign Out Error", error);
        });
    },
    isUserLoggedIn: () => {
        return auth.currentUser != null;
    },
    updateProfile: user => {
        let promise = auth.currentUser.updateProfile(user);
        promise.then(() => {
            // Update the logged user BehaviourSubject with new values
            let loggedUser = service.loggedUser.value;
            loggedUser.displayName = user.displayName;
            loggedUser.photoURL = user.photoURL;
        });
        return promise;
    },
    updateEmail: async (currentPassword, newEmail) => {
        const credential = EmailAuthProvider.credential(
            auth.currentUser.email,
            currentPassword
        );
        await auth.currentUser.reauthenticateWithCredential(credential);
        let promise = auth.currentUser.updateEmail(newEmail);
        promise.then(() => {
            // Update the logged user BehaviourSubject with new values
            let loggedUser = service.loggedUser.value;
            loggedUser.email = newEmail;
        });
        return promise;
    },
    updatePassword: async (currentPassword, newPassword) => {
        const credential = EmailAuthProvider.credential(
            auth.currentUser.email,
            currentPassword
        );
        await auth.currentUser.reauthenticateWithCredential(credential);
        return auth.currentUser.updatePassword(newPassword);
    },
    refreshCurrentUser: (user) => {
        auth.currentUser.getIdToken(true);
        // Emit a new user value to subscribers via loggedUser
        service.loggedUser.next(user);
    },
    /**
     * Reload the current authed user. This will log them out if they have been disabled.
     */
    checkUserAuth: async() => {
        // await auth.currentUser.getIdToken(true);
        await auth.currentUser.reload()
    },
    resetPassword: (emailAddress) => {
        var actionCodeSettings = {
            // After password reset, the user will be give the ability to go back to this page.
            url: `${window.location.origin}/#/signin`,
            handleCodeInApp: false,
        };
        return auth.sendPasswordResetEmail(emailAddress, actionCodeSettings);
    },

    // Expose an AuthService property which is an Observable on the loggedUser.
    // Observers can subscribe to this, but cannot emit new values to it.
    loggedUser$: service.loggedUser.asObservable()
}

export {
  AuthService
}
